import React, {useState} from 'react';
import {Box, Tooltip} from '@chakra-ui/react';
import {useHalState} from '@synako/haligator';
import {Navigable, State} from '@synako/halitrek';
import {LigneDePanierDesc} from 'api-types/ffconnect-hal';
import {useTranslation} from 'react-i18next';
import {toDecimalFr, useLivraisonsPossiblesContext} from 'ff-common';
import {useClient} from '@/haligator-factory';

/**
 * Tooltip du prix appliqué après la fin de la promotion, au survol.
 * Réservé à la prise de commande commerciale : les liens sont filtrés par
 * `voir-prix-post-promo`, donc absents côté client.
 *
 * - Ligne de panier : lien `prix-post-promotion` de la ligne, comme avant.
 * - Hors panier : lien `prix-post-promotion-article`, seulement si `estPromo`.
 *
 * L'appel Tolède n'est suivi qu'au premier survol, pas au montage.
 */

interface PrixPostPromotionTooltipProps {
  ligneDePanier?: State<LigneDePanierDesc>;
  /** Hors panier : code article pour suivre `prix-post-promotion-article`. */
  codeArticle?: string;
  /** Prix affiché en promo. Sans ligne de panier, seul cas qui déclenche l'appel. */
  estPromo?: boolean;
  shouldShow: boolean;
  children: React.ReactNode;
}

export const PrixPostPromotionTooltip: React.FC<
  PrixPostPromotionTooltipProps
> = ({ligneDePanier, codeArticle, estPromo = false, shouldShow, children}) => {
  const {t} = useTranslation();
  // Survol suivi à part de Chakra. Le tooltip est contrôlé : tant que Tolède
  // n'a pas répondu, isOpen reste false. Si on s'appuyait sur onOpen/onClose,
  // Chakra rappellerait onClose pendant ce pending et le prix arrivé en 200
  // ne s'afficherait plus.
  const [survole, setSurvole] = useState(false);
  const [loadPrix, setLoadPrix] = useState(false);
  // Remonte le chargeur après une erreur Tolède : sinon le hook garde l'échec
  // et le survol suivant ne rappelle pas.
  const [essai, setEssai] = useState(0);
  const client = useClient();
  const {date_de_livraison} = useLivraisonsPossiblesContext();

  const lienLigne = ligneDePanier?.hasOneLink('prix-post-promotion') ?? false;
  const chercheArticle = !lienLigne && estPromo && !!codeArticle;
  const clientState = useHalState(chercheArticle ? client : undefined);
  const lienArticle =
    chercheArticle &&
    !!date_de_livraison &&
    (clientState.state?.hasOneLink('prix-post-promotion-article') ?? false);

  const nav =
    loadPrix && lienLigne
      ? ligneDePanier?.follow('prix-post-promotion')
      : loadPrix &&
          lienArticle &&
          codeArticle &&
          date_de_livraison &&
          clientState.state
        ? clientState.state.follow('prix-post-promotion-article', {
            code_article: codeArticle,
            date_de_livraison,
          })
        : undefined;

  if (!shouldShow || (!lienLigne && !lienArticle)) {
    return <Tooltip isDisabled>{children}</Tooltip>;
  }

  return (
    <TooltipPrix
      key={essai}
      nav={nav}
      survole={survole}
      label={(prix) =>
        t('back_office.prise_de_commande.panier.prix_post_promo', {
          prix: toDecimalFr(prix, 2, 3),
        })
      }
      onEnter={(enErreur) => {
        setSurvole(true);
        setLoadPrix(true);
        if (enErreur) setEssai((n) => n + 1);
      }}
      onLeave={() => {
        setSurvole(false);
      }}
    >
      {children}
    </TooltipPrix>
  );
};

/** Isolé pour pouvoir le remonter (`key`) après une erreur et relancer le GET. */
const TooltipPrix: React.FC<{
  nav: Navigable | undefined;
  survole: boolean;
  label: (prix: number) => string;
  onEnter: (enErreur: boolean) => void;
  onLeave: () => void;
  children: React.ReactNode;
}> = ({nav, survole, label, onEnter, onLeave, children}) => {
  const prixState = useHalState(nav);
  const prixPostPromo = prixState.data?.prix_post_promo;

  return (
    <Tooltip
      isOpen={survole && prixPostPromo != null}
      label={prixPostPromo != null ? label(prixPostPromo) : ''}
      placement='top'
      borderRadius='4px'
      hasArrow
    >
      <Box
        onMouseEnter={() => {
          onEnter(!!prixState.error);
        }}
        onMouseLeave={onLeave}
        w='100%'
      >
        {children}
      </Box>
    </Tooltip>
  );
};
